// Detects when a newer build of the portal has been deployed while this tab
// is still running an older one, and surfaces it via UpdateAvailableBanner.vue.
//
// Covers every way a tab can end up stale:
//  - actively being used when a new version ships -> periodic poll
//  - left open/idle for a long time, or backgrounded -> the poll still runs
//    (browsers throttle background timers, they don't stop them), and a
//    visibilitychange/focus check catches up immediately the moment the
//    user comes back to this tab or window
//  - several tabs open at once -> the tab that notices first writes to
//    localStorage, and every other tab's `storage` listener reacts within
//    the same tick, so they don't each wait out their own poll interval
import { ref } from 'vue'

const CHECK_INTERVAL_MS = 3 * 60 * 1000 // 3 minutes
const BROADCAST_KEY = 'yandle_portal_update_available'

// What THIS tab was actually built with (baked in at build time -- see
// scripts/generate-version.js / .env.production.local), not just whatever
// the first poll happens to return. Falls back to a random per-load value
// in dev (no VUE_APP_VERSION set), so local dev without a real build never
// falsely claims an update is available against itself.
const CURRENT_VERSION = process.env.VUE_APP_VERSION || null

export const updateAvailable = ref(false)
export const latestVersionInfo = ref(null)

let pollTimer = null
let started = false

async function fetchVersionInfo() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    if (!res.ok) return null
    return await res.json()
  } catch {
    // Offline, blocked, or version.json not deployed yet -- just skip this
    // tick, the next poll will try again.
    return null
  }
}

function announce(info) {
  updateAvailable.value = true
  latestVersionInfo.value = info
  try {
    // The value only needs to change to fire `storage` in other tabs --
    // content isn't read back from it, each tab re-fetches version.json
    // itself once notified, so it always shows its own fresh copy.
    localStorage.setItem(BROADCAST_KEY, String(Date.now()))
  } catch {
    // Private browsing / storage disabled -- this tab still shows its own
    // banner, it just can't wake up sibling tabs.
  }
}

async function checkNow() {
  if (updateAvailable.value || !CURRENT_VERSION) return
  const info = await fetchVersionInfo()
  if (info && info.version && info.version !== CURRENT_VERSION) {
    announce(info)
  }
}

export function startVersionCheck() {
  if (started) return
  started = true

  checkNow()
  pollTimer = setInterval(checkNow, CHECK_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkNow()
  })
  window.addEventListener('focus', checkNow)
  window.addEventListener('storage', (event) => {
    if (event.key === BROADCAST_KEY && event.newValue) checkNow()
  })
}

export function stopVersionCheck() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
  started = false
}

// A plain reload can still be served from the browser's back-forward cache
// or an overly-eager disk cache; forcing a fresh URL guarantees a real
// network round-trip to the no-cache index.html rather than trusting the
// cache to do the right thing.
export function hardRefresh() {
  const url = new URL(window.location.href)
  url.searchParams.set('_v', Date.now().toString())
  window.location.href = url.toString()
}
